import React, { useEffect, useState } from 'react';
import { superApi, useSuperAdminAuth } from './SuperAdminAuth';
import { toast } from 'sonner';
import { Building2, Flag, ExternalLink, ShieldAlert, Eye } from 'lucide-react';

/** Companies list with impersonate + feature flags. */
export const SuperCompaniesPage = () => {
  const { hasPerm } = useSuperAdminAuth();
  const [companies, setCompanies] = useState([]);
  const [flagCompany, setFlagCompany] = useState(null);

  const load = async () => {
    try {
      const r = await superApi.get('/super/companies');
      setCompanies(r.data.companies || []);
    } catch (e) { toast.error('Failed to load companies'); }
  };
  useEffect(() => { load(); }, []);

  const impersonate = async (c) => {
    if (!window.confirm(`Impersonate admin of "${c.name}"? Session lasts 30 minutes and every action is audited.`)) return;
    try {
      const r = await superApi.post(`/super/impersonate/${c.id}`);
      // Swap the regular tenant token and navigate to /dashboard
      localStorage.setItem('token', r.data.token);
      localStorage.setItem('impersonation_active', JSON.stringify({
        until: r.data.expires_at,
        company: c.name,
      }));
      // Also set the user object so AuthContext recognizes the session
      if (r.data.impersonating) {
        const impUser = {
          id: r.data.impersonating.user_id,
          email: r.data.impersonating.email,
          company_id: r.data.impersonating.company_id,
          full_name: r.data.impersonating.company_name + ' Admin',
          roles: ['admin'],
          is_admin: true,
        };
        localStorage.setItem('user', JSON.stringify(impUser));
      }
      toast.success(`Impersonating ${c.name}`);
      window.location.href = '/dashboard';
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Impersonation failed');
    }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="mb-6 flex items-center gap-2">
        <Building2 className="w-6 h-6 text-amber-400" />
        <h1 className="text-2xl font-semibold text-white">Companies</h1>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-950 text-slate-400">
            <tr>
              <th className="text-left p-3">Name</th>
              <th className="text-left p-3">Slug</th>
              <th className="text-left p-3">Users</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Created</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {companies.map((c) => (
              <tr key={c.id} className="border-t border-slate-800 hover:bg-slate-950/60">
                <td className="p-3 text-white font-medium">{c.name}</td>
                <td className="p-3 text-slate-400 font-mono text-xs">{c.slug || c.id}</td>
                <td className="p-3 text-slate-300">{c.user_count || 0}</td>
                <td className="p-3">
                  {c.is_suspended ? (
                    <span className="text-red-400 text-xs"><ShieldAlert className="w-3 h-3 inline mr-1" />Suspended</span>
                  ) : (
                    <span className="text-green-400 text-xs">Active</span>
                  )}
                </td>
                <td className="p-3 text-slate-500 text-xs">{c.created_at?.slice(0, 10)}</td>
                <td className="p-3 text-right">
                  <div className="inline-flex gap-1">
                    {hasPerm('super.feature_flags.view') && (
                      <button onClick={() => setFlagCompany(c)} className="p-1.5 text-slate-400 hover:text-amber-400" title="Feature flags">
                        <Flag className="w-4 h-4" />
                      </button>
                    )}
                    {hasPerm('super.companies.impersonate') && (
                      <button onClick={() => impersonate(c)} className="p-1.5 text-slate-400 hover:text-amber-400" title="View as this company's admin">
                        <Eye className="w-4 h-4" />
                      </button>
                    )}
                    <a href={`/superadmin/companies/${c.id}`} className="p-1.5 text-slate-400 hover:text-amber-400" title="Open company settings">
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {companies.length === 0 && <div className="p-8 text-center text-slate-500 text-sm">No companies yet.</div>}
      </div>

      {flagCompany && <FeatureFlagsModal company={flagCompany} onClose={() => setFlagCompany(null)} />}
    </div>
  );
};

const FeatureFlagsModal = ({ company, onClose }) => {
  const { hasPerm } = useSuperAdminAuth();
  const canManage = hasPerm('super.feature_flags.manage');
  const [flags, setFlags] = useState({});
  const [loading, setLoading] = useState(true);

  // Well-known keys + defaults; users can add more.
  const WELL_KNOWN = [
    { key: 'beta_journeys', label: 'Beta: new Journey Builder features' },
    { key: 'beta_broadcasts', label: 'Beta: enhanced Broadcasts' },
    { key: 'abandoned_polling', label: 'Abandoned-checkout polling (15-min)' },
    { key: 'auto_courier_assign', label: 'Courier auto-assign rules' },
    { key: 'ai_suggestions', label: 'AI reply suggestions' },
  ];

  useEffect(() => {
    (async () => {
      try {
        const r = await superApi.get(`/super/companies/${company.id}/feature-flags`);
        setFlags(r.data.flags || {});
      } catch (e) { /* ignore */ }
      setLoading(false);
    })();
  }, [company.id]);

  const save = async () => {
    try {
      await superApi.put(`/super/companies/${company.id}/feature-flags`, { flags });
      toast.success('Flags saved');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="text-lg font-semibold text-white mb-1">Feature Flags</div>
        <div className="text-xs text-slate-400 mb-4">{company.name}</div>
        {loading ? <div className="text-slate-500 text-sm">Loading…</div> : (
          <div className="space-y-2">
            {WELL_KNOWN.map((f) => (
              <label key={f.key} className="flex items-start gap-3 p-3 bg-slate-950 rounded-lg border border-slate-800 cursor-pointer">
                <input
                  type="checkbox"
                  disabled={!canManage}
                  checked={!!flags[f.key]}
                  onChange={(e) => setFlags({ ...flags, [f.key]: e.target.checked })}
                  className="mt-1"
                />
                <div>
                  <div className="text-sm text-slate-200">{f.label}</div>
                  <div className="text-[10px] text-slate-500 font-mono">{f.key}</div>
                </div>
              </label>
            ))}
          </div>
        )}
        <div className="mt-4 flex gap-2">
          <button onClick={onClose} className="flex-1 py-2 rounded-lg border border-slate-800 text-slate-300 hover:bg-slate-800">Close</button>
          {canManage && <button onClick={save} className="flex-1 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-medium">Save</button>}
        </div>
      </div>
    </div>
  );
};

export default SuperCompaniesPage;
